/**
 * <recommendation-slider>
 * PDP product recommendations carousel using native CSS scroll.
 * Mobile: touch scroll, controls hidden via media query.
 * Desktop: click prev/next jumps one slide width; aria-disabled at boundaries.
 */
class RecommendationSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-rs-track]");
    if (!this.track) return;

    // Controls live as a sibling outside this element. Look up within the
    // PDP recommendations section to scope the lookup (avoids cross-section
    // bleed if multiple recommendation blocks ever coexist).
    const section = this.closest(".product-recommendations");
    this.prevBtn = section?.querySelector("[data-rs-prev]");
    this.nextBtn = section?.querySelector("[data-rs-next]");

    // Defensive: reset scroll position. Guards against iOS RTL inheritance
    // / mandatory snap initial-layout quirks (this carousel has no snap but
    // RTL still flips initial scrollLeft).
    this.track.scrollLeft = 0;

    this.controller = new AbortController();
    const { signal } = this.controller;
    this.reduceMotionMq = window.matchMedia("(prefers-reduced-motion: reduce)");

    this.prevBtn?.addEventListener("click", () => this.scrollByOne(-1), { signal });
    this.nextBtn?.addEventListener("click", () => this.scrollByOne(1), { signal });
    this.track.addEventListener("scroll", () => this.updateButtonState(), { signal, passive: true });
    window.addEventListener("resize", () => this.updateButtonState(), { signal, passive: true });

    this.updateButtonState();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  scrollByOne(direction) {
    const slide = this.track.querySelector("[data-rs-slide]");
    if (!slide) return;
    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    this.track.scrollBy({
      left: (slide.offsetWidth + gap) * direction,
      behavior: this.reduceMotionMq.matches ? "auto" : "smooth",
    });
  }

  updateButtonState() {
    if (!this.track) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= max - 1 || max <= 0;
    this.prevBtn?.setAttribute("aria-disabled", String(atStart));
    this.nextBtn?.setAttribute("aria-disabled", String(atEnd));
  }
}

if (!window.customElements.get("recommendation-slider")) {
  window.customElements.define("recommendation-slider", RecommendationSlider);
}
